import { Loader2, ShieldOff } from "lucide-react";

import usePermissions from "../../hooks/usePermissions";
import MainContainer from "../MainContainer";

// Qual permissão cada tela exige.
//
// Precisa espelhar o `can()` das rotas do backend: é ele quem barra de fato,
// aqui só evitamos a tela abrir. Sem esta camada, quem não tinha acesso a
// Conexões chegava lá digitando a URL — o menu escondia o item, e esconder
// não é impedir.
//
// A chave é a mesma do backend, de propósito. Duas nomenclaturas para a mesma
// permissão seria a garantia de que um dia elas divergem.
// Cada valor é uma lista: basta ter UMA das permissões, igual ao `can()` do
// backend, que aceita várias. Guardar `/users` só com "users:create" trancaria
// quem pode editar mas não criar.
//
// Só entram telas cuja rota de backend realmente exige permissão. Etiquetas,
// por exemplo, não têm `can()` em nenhuma rota — barrar aqui inventaria uma
// regra que o servidor não aplica e quebraria quem hoje usa a tela.
const PERMISSAO_POR_ROTA = {
  "/": ["dashboard:view"],
  "/operational-panel": ["dashboard:metrics", "supervision:monitor"],
  "/reports": ["dashboard:reports"],
  "/connections": ["connections:manage"],
  "/users": ["users:create", "users:edit", "users:delete"],
  "/roles": ["users:permissions"],
  "/queues": ["queues:create", "queues:edit", "queues:delete"],
  "/teams": ["users:edit"],
  "/departments": ["users:edit"],
  "/webhooks": ["system:integrations"],
  "/queue-integrations": ["system:integrations"],
  "/messages-api": ["system:integrations"],
  "/modelos-whatsapp": ["system:integrations"],
  "/zernio": ["system:integrations"],
  "/campaigns-config": ["campaigns:create", "campaigns:edit"],
  "/campaigns-phrases": ["campaigns:create", "campaigns:edit"],
  "/global-config": ["system:settings"],
  "/settings": ["system:settings"],
  "/financeiro": ["dashboard:financial"],
  "/saas/finance": ["dashboard:financial"],
  "/flows": ["flows:manage", "system:integrations"]
};

// A rota mais específica ganha: "/saas/finance" antes de "/saas".
export const permissoesDaRota = (caminho) => {
  const alvo = String(caminho || "").toLowerCase();

  const chave = Object.keys(PERMISSAO_POR_ROTA)
    .sort((a, b) => b.length - a.length)
    .find((prefixo) => {
      const p = prefixo.toLowerCase();

      return alvo === p || alvo.startsWith(`${p}/`);
    });

  return chave ? PERMISSAO_POR_ROTA[chave] : null;
};

const PermissionGuard = ({ caminho, children }) => {
  const { temPermissao, carregando } = usePermissions();

  const exigidas = permissoesDaRota(caminho);

  if (!exigidas?.length) return children;

  if (carregando) {
    return (
      <MainContainer>
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </MainContainer>
    );
  }

  // Basta uma, como no backend.
  if (exigidas.some((chave) => temPermissao(chave))) return children;

  // Diz o que aconteceu e a quem pedir. Uma tela em branco, ou um erro do
  // backend a cada clique, faria a pessoa achar que o sistema quebrou.
  return (
    <MainContainer>
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-16 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <ShieldOff className="h-6 w-6" />
        </span>
        <p className="mt-4 text-base font-medium">
          Esta tela não faz parte do seu acesso
        </p>
        <p className="mt-1 max-w-md text-sm text-muted-foreground">
          Quem administra a conta pode liberar em Cargos e permissões. Nada foi
          alterado e ninguém foi avisado — você só não tem acesso a esta parte
          do sistema.
        </p>
      </div>
    </MainContainer>
  );
};

export default PermissionGuard;
